import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { AdminClinicLocationService } from '../../services/admin/admin-clinic-location.service';
import styled from 'styled-components';
import OvClinicLocationUserList from '../UI/organisms/OvClinicLocationUserList';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvClinicLocationPatientSearchFilter from '../UI/organisms/OvClinicLocationPatientSearchFilter';
import OvButton from '../UI/atoms/OvButton';
import Variables from '../../design-system/variables';
import breakpoints from '../../design-system/breakpoints';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AssignPatientToClinicLocationRequest } from '../../common/model/dto/assign-user-to-clinic-location-request';
import OvAssignPatientToClinicLocationDialog from '../UI/molecules/OvAssignPatientToClinicLocationDialog';
import Colours from '../../design-system/colours';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import OvCreateDemoAccountDialog from '../UI/molecules/OvCreateDemoAccountDialog';
import { CreateDemoAccountRequest } from '../../common/model/dto/create-demo-account-request';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { isEmpty } from 'lodash';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useAdminClinicLocationMutations } from '../../hooks/use-admin-clinic-location-mutations';
import { useNavigate } from 'react-router-dom';
import { FilterModel } from '../../common/model/ui/filter.model';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { OrderBy, SortDirection } from '../../common/types';
import { ClinicLocationUtils } from '../../common/utils/services/clinic-location-utils';

const CLINIC_LOCATION_USER_LIST_LIMIT = 10;

const FilterableClinicLocationPatientsList: FC<{
  selectedClinicLocation?: ClinicLocation;
  clinicId: string;
}> = ({ selectedClinicLocation, clinicId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicPatientOperations = useAuthorized(Resource.ClinicPatient);
  const demoAccountOperations = useAuthorized(Resource.DemoAccount);

  const clinicPatientCreateAllowed =
    clinicPatientOperations.create || applicationOperations.supervise;
  const clinicPatientUpdateAllowed =
    clinicPatientOperations.update || applicationOperations.supervise;

  const clinicPatientDeleteAllowed =
    clinicPatientOperations.delete || applicationOperations.supervise;
  const demoAccountCreateAllowed =
    demoAccountOperations.create || applicationOperations.supervise;

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCreateDemoAccountDialogOpen, setIsCreateaDemoAccountDialogOpen] =
    useState(false);

  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const [providerId, setProviderId] = useQueryParam(
    'provider_id',
    withDefault(StringParam, '')
  );

  const [knownReproductiveDisorder, setKnownReproductiveDisorder] =
    useQueryParam('known_reproductive_disorder', withDefault(StringParam, ''));

  const [shouldHideNotRegisteredUsers, setShouldHideNotRegisteredUsers] =
    useQueryParam(
      'should_hide_not_registered_users',
      withDefault(StringParam, '')
    );

  const query = queryString.stringify({
    limit: CLINIC_LOCATION_USER_LIST_LIMIT,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
    offset,
    provider_id: providerId,
    known_reproductive_disorder: knownReproductiveDisorder,
    should_hide_not_registered_users: shouldHideNotRegisteredUsers,
  });

  const {
    data: paginatedPatients,
    isPending: isGetPatientsPending,
    isFetching: isGetPatientsFetching,
    error: getPatientsError,
  } = useQuery({
    queryKey: ['admin-clinic-location-patients', query],
    queryFn: () =>
      AdminClinicLocationService.getClinicLocationPatients(
        query,
        selectedClinicLocation?.id
      ),
    retry: 3,
    enabled: !!selectedClinicLocation?.id && !!orderBy && !!orderDir,
    placeholderData: keepPreviousData,
  });

  const {
    assignPatientToClinicLocation,
    createDemoAccount,
    removePatientFromClinicLocation,
    approvePatientInvitationForUser,
  } = useAdminClinicLocationMutations(selectedClinicLocation?.id ?? '');

  const patients = paginatedPatients?.docs ?? [];
  const total = paginatedPatients?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('patient_added_at');
      setOrderDir('desc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const activeSortOption =
    ClinicLocationUtils.getClinicLocationPatientSortingOptions().find(
      (option) => option.value === orderBy
    );

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * CLINIC_LOCATION_USER_LIST_LIMIT);
  };

  const handleOnSave = async (
    request: AssignPatientToClinicLocationRequest
  ) => {
    setIsDialogOpen(false);
    const res = await assignPatientToClinicLocation.mutateAsync(request);

    navigate(`/users/${res.user.id}`);
  };

  const handleOnCreateDemoAccount = async (
    createDemoAccountRequest: CreateDemoAccountRequest
  ) => {
    setIsCreateaDemoAccountDialogOpen(false);
    const res = await createDemoAccount.mutateAsync(createDemoAccountRequest);

    navigate(`/users/${res.user.id}`);
  };

  const onFilterChange = (filter: FilterModel) => {
    switch (filter.fieldName) {
      case 'provider_id':
        setProviderId(filter.value);
        break;

      case 'known_reproductive_disorder':
        setKnownReproductiveDisorder(filter.value);
        break;

      case 'should_hide_not_registered_users':
        setShouldHideNotRegisteredUsers(filter.value);
        break;

      default:
        break;
    }
  };

  const onFilterClear = () => {
    setProviderId(null);
    setKnownReproductiveDisorder(null);
    setShouldHideNotRegisteredUsers(null);
  };

  const onSortByColumn = (orderBy: OrderBy) => {
    setOrderBy(orderBy.order_by);
    setOrderDir(orderBy.order_dir);
  };

  const isLoading =
    isGetPatientsPending ||
    isGetPatientsFetching ||
    assignPatientToClinicLocation.isPending ||
    createDemoAccount.isPending ||
    removePatientFromClinicLocation.isPending ||
    approvePatientInvitationForUser.isPending;

  return (
    <Container>
      <CenterPane>
        <UserActions>
          <OvPaginationBar
            total={total}
            limit={CLINIC_LOCATION_USER_LIST_LIMIT}
            offset={offset}
            onChange={goToPage}
          />

          <ButtonWrapper>
            {clinicPatientCreateAllowed && (
              <StyledOvButton onClick={() => setIsDialogOpen(true)}>
                <AddCircleIcon style={{ marginRight: '5' }} />
                {t('clinicLocations.actions.addNewPatient')}
              </StyledOvButton>
            )}

            {demoAccountCreateAllowed && (
              <StyledOvButton
                onClick={() => setIsCreateaDemoAccountDialogOpen(true)}
              >
                <PersonAddAltIcon style={{ marginRight: '5' }} />
                {t('common.actions.createDemoAccount')}
              </StyledOvButton>
            )}
          </ButtonWrapper>
        </UserActions>

        {getPatientsError && (
          <OvErrorMessage message={getPatientsError?.message} />
        )}

        {patients?.length ? (
          <OvClinicLocationUserList
            users={patients}
            clinicLocationId={selectedClinicLocation?.id}
            clinicId={clinicId}
            deleteAction={removePatientFromClinicLocation}
            deleteActionCtaLabel={t('clinicLocationDetails.actions.removeUser')}
            showClinicLocationPatientCreatedAt={true}
            approveInvitationAction={approvePatientInvitationForUser}
            approveInvitationCtaLabel={t(
              'clinicLocationDetails.actions.acceptInvitation'
            )}
            editable={clinicPatientUpdateAllowed}
            deletable={clinicPatientDeleteAllowed}
            tableHeaders={ClinicLocationUtils.getAdminClinicLocationPatientListTableHeaders()}
            onSort={onSortByColumn}
            orderBy={orderBy}
            orderDir={orderDir as SortDirection}
            currentSortColumn={activeSortOption?.value}
          />
        ) : (
          ''
        )}

        {!patients?.length && !isLoading ? (
          <OvNoContent>
            {t('clinicLocationDetails.emptyList.patients')}
          </OvNoContent>
        ) : null}
        {isLoading && <OvLoadingIndicator position="fixed" />}
      </CenterPane>

      {selectedClinicLocation && (
        <OvClinicLocationPatientSearchFilter
          clinicLocationId={selectedClinicLocation.id ?? ''}
          removeFilters={onFilterClear}
          onHandleFilterValueChange={onFilterChange}
          filterValues={{
            providerId,
            knownReproductiveDisorder,
            shouldHideNotRegisteredUsers,
          }}
        />
      )}

      <OvAssignPatientToClinicLocationDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
        title={t('clinicLocationDialogs.assignPatientTitle')}
      />

      <OvCreateDemoAccountDialog
        isOpen={isCreateDemoAccountDialogOpen}
        onCancel={() => setIsCreateaDemoAccountDialogOpen(false)}
        onSave={handleOnCreateDemoAccount}
        title={t('common.actions.createDemoAccount')}
        enableCreatePassword={true}
      />
    </Container>
  );
};

export default FilterableClinicLocationPatientsList;

const Container = styled.section`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
  min-width: 13rem;
  display: flex;
  flex-wrap: 'wrap';
  justify-content: flex-end;
  @media (min-width: ${breakpoints.md}) {
    min-width: auto;
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    padding: 2px 0.625rem 2px 0.25rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    background-color: ${Colours.OV_BASE};

    &:last-child {
      margin-right: 0;
    }
  }
`;
